import { type CollisionDetection, closestCenter } from '@dnd-kit/core';

import { readPinnedBands } from '../Table/utils';

// Collision detection for closest top center of dropzone to center of draggable
export function closestTopCenterCollisionDetection(
  args: Parameters<CollisionDetection>[0],
): ReturnType<CollisionDetection> {
  // Get the active draggable's rect from collisionRect
  const draggableRect = args.collisionRect;

  if (!draggableRect) {
    // Fallback to default collision detection if draggable rect is not available
    return closestCenter(args);
  }

  // Calculate the center point of the draggable rectangle
  const draggableCenterX = draggableRect.left + draggableRect.width / 2;
  const draggableCenterY = draggableRect.top + draggableRect.height / 2;

  // Get all collisions using the default detection (no section filtering)
  const allCollisions = closestCenter(args);
  if (allCollisions.length === 0) {
    return allCollisions;
  }

  // Get rects for all collisions and calculate distances
  const collisionsWithDistances = allCollisions
    .map((collision) => {
      const droppableRect = args.droppableRects.get(collision.id);
      if (!droppableRect) return null;

      // Calculate the top center point of the droppable rectangle
      const droppableTopCenterX = droppableRect.left + droppableRect.width / 2;
      const droppableTopCenterY = droppableRect.top;

      // Calculate the distance from draggable center to droppable top center
      const deltaX = draggableCenterX - droppableTopCenterX;
      const deltaY = draggableCenterY - droppableTopCenterY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      return { collision, distance };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (collisionsWithDistances.length === 0) {
    return allCollisions;
  }

  // Find the collision with the minimum distance
  const closestCollision = collisionsWithDistances.reduce((closest, current) =>
    current.distance < closest.distance ? current : closest,
  );

  return [closestCollision.collision];
}

const HIDE_COLUMN_DROPZONE_ID = 'hide-column-dropzone';

type DroppableContainer = Parameters<CollisionDetection>[0]['droppableContainers'][number];
type PinnedRects = ReturnType<typeof readPinnedBands>;

// An unpinned target is hidden when its left edge (the insertion point) sits under the sticky pinned columns.
function isUnderPinnedSection(node: HTMLElement, rect: DOMRect, cache: Map<Element, PinnedRects>): boolean {
  if (node.closest('[data-pinned]')) return false;
  const sections = node.closest('[data-slot="table-sections"]');
  if (!sections) return false;
  let pinned = cache.get(sections);
  if (!pinned) {
    pinned = readPinnedBands(sections);
    cache.set(sections, pinned);
  }
  const { left, right } = pinned;
  return (
    (left !== undefined && rect.left >= left.left && rect.left < left.right) ||
    (right !== undefined && rect.left >= right.left && rect.left <= right.right)
  );
}

/**
 * Column-reorder collision detection: picks the droppable whose left edge is nearest the pointer
 * (or the dragged rect's center for keyboard drags), and the hide-column dropzone when the pointer
 * is within or above it. Rects are measured live because dnd-kit shifts its recorded rects by
 * scroll deltas, which drift for sticky pinned headers that stay put while the container scrolls.
 */
export function closestLeftEdgeCollisionDetectionWithHideColumnDropzone(
  args: Parameters<CollisionDetection>[0],
): ReturnType<CollisionDetection> {
  const { pointerCoordinates: pointer, collisionRect, droppableContainers, droppableRects } = args;

  if (pointer) {
    const hideRect = droppableRects.get(HIDE_COLUMN_DROPZONE_ID);
    const hideContainer = droppableContainers.find((c) => c.id === HIDE_COLUMN_DROPZONE_ID && !c.disabled);
    // 10px buffer so the pointer has to clearly enter the dropzone
    if (hideRect && hideContainer && pointer.y <= hideRect.bottom - 10) {
      return [{ id: hideContainer.id, data: { droppableContainer: hideContainer, value: 0 } }];
    }
  }

  const referenceX = pointer ? pointer.x : collisionRect.left + collisionRect.width / 2;
  const pinnedCache = new Map<Element, PinnedRects>();
  let closest: { container: DroppableContainer; distance: number } | null = null;

  for (const container of droppableContainers) {
    if (container.id === HIDE_COLUMN_DROPZONE_ID || container.disabled) continue;
    const node = container.node.current;
    if (!node) continue;
    const rect = node.getBoundingClientRect();
    if (isUnderPinnedSection(node, rect, pinnedCache)) continue;
    const distance = Math.abs(referenceX - rect.left);
    if (!closest || distance < closest.distance) closest = { container, distance };
  }

  return closest
    ? [{ id: closest.container.id, data: { droppableContainer: closest.container, value: closest.distance } }]
    : [];
}
